/**
 * Taxonomy + URL parsing for outbound navigations to the StarZero Agents app
 * (`CHAT_URL`, e.g. https://chat.starzero.ai).
 *
 * Why this exists: every handoff to the agents app is a cross-origin
 * navigation, so Mixpanel reads it as idle time or a session exit. Without a
 * label, the dashboard can't answer "where did this session actually go?".
 * `openAuthedChatUrl` is the chokepoint 18 of the 20 exits funnel through, and
 * it receives the fully-built URL — so the destination is *derived* here rather
 * than threaded through every call site. Call sites only supply the surface,
 * the control, and the human-readable names.
 *
 * Deliberately free of store/React imports so it can be unit-tested with the
 * project's standalone `tsx` runner:
 *   npx tsx src/lib/analytics/__tests__/agentsExit.test.ts
 */

/** A page in the agents app we can send the user to. */
export type AgentsDestination =
  | 'new-chat'
  | 'existing-chat'
  | 'chats-list'
  | 'artifacts'
  | 'work-with-library'
  | 'work-with-media'
  | 'work-with-moments'
  | 'podcasts-list'
  | 'podcast-create'
  | 'podcast-project'
  | 'customize'
  | 'agents-home'
  | 'other';

/** Which area of *our* app the user left from. */
export type AgentsSurface =
  | 'sidebar'
  | 'workspace'
  | 'libraries'
  | 'library'
  | 'search'
  // Story Atlas. Added when the moment modal's action bar reached `/atlas`: before that the
  // modal deliberately offered no agent handoff there, precisely because there was no member
  // to attribute it to. Reporting those clicks as `'library'` would have quietly folded a
  // distinct surface into the library's numbers.
  | 'atlas'
  | 'podcasts-page'
  | 'auth';

/**
 * What a call site contributes. Everything except `surface`/`control` is
 * optional — pass only what's already in scope at that component.
 *
 * IDs and counts ONLY. Deliberately no `libraryName` / `mediaName` /
 * `chatTitle` / `podcastName`: those are user-authored free text and count as
 * personally identifiable information — media names are raw upload filenames,
 * which routinely carry client and campaign names. The IDs answer "where did
 * this session go?" on their own and can be joined to names after the fact by
 * anyone who already has data access. Do not add name fields here.
 */
export interface AgentsExitContext {
  surface: AgentsSurface;
  /** Kebab-case identifier of the specific control, e.g. `library-kebab`. */
  control: string;
  libraryId?: string;
  /** Single-media exits only. Bulk exits send `mediaCount` instead. */
  mediaId?: string;
  mediaCount?: number;
  momentCount?: number;
  chatId?: string;
  podcastId?: string;
  /** Position within a gallery or list, when the control is one of many. */
  itemIndex?: number;
}

/**
 * Compile-time guard for the no-PII rule above.
 *
 * `AssertNever` only accepts `never`, so if anyone adds a key like
 * `libraryName`, `mediaName`, `chatTitle` or `podcastName` to
 * `AgentsExitContext`, `NameLikeKeys` stops being `never` and this line fails
 * to compile — with the offending key named in the error. A runtime test can't
 * catch this (an optional field is invisible to a sample object), so the type
 * system has to.
 *
 * If you hit this error: the fix is to send an ID, not to widen the guard.
 */
type AssertNever<T extends never> = T;
type NameLikeKeys = Extract<
  keyof AgentsExitContext,
  `${string}Name` | `${string}Title` | 'name' | 'title'
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _NoUserAuthoredStringsInExitContext = AssertNever<NameLikeKeys>;

export interface AgentsDestinationInfo {
  destination: AgentsDestination;
  /** Human-readable label, so dashboards read without a lookup table. */
  destinationLabel: string;
  /** Whether the URL is on the agents-app origin. */
  isAgentsApp: boolean;
  /** Hostname, or `''` when the URL couldn't be parsed. */
  targetHost: string;
}

const DESTINATION_LABELS: Record<AgentsDestination, string> = {
  'new-chat': 'New chat',
  'existing-chat': 'Went to existing chat',
  'chats-list': 'Chats list',
  artifacts: 'Artifacts',
  'work-with-library': 'Work with an AI Agent',
  'work-with-media': 'Work with an Agent on media',
  'work-with-moments': 'Work with an Agent on clips',
  'podcasts-list': 'Podcast Clipper',
  // Matches the UI copy ("Clip as a podcast"). Renamed after #802 shipped, so charts
  // built on the old label will show a break at that point; the `destination` key is
  // unchanged, so anything grouping on that is unaffected.
  'podcast-create': 'Clip as a podcast',
  'podcast-project': 'Opened podcast project',
  customize: 'Customize agents',
  'agents-home': 'Agents home',
  other: 'Agents app',
};

/**
 * Mirrors the fallback in `starzero-agents.ts` — that module still navigates
 * using it when `CHAT_URL` is unset, so it counts as an agents origin here.
 * (`chats.ts` / `podcasts.ts` degrade to `''` and render no links instead.)
 */
const AGENTS_ORIGIN_FALLBACK = 'https://chat.vibedit.ai/';

function hostOf(url: string): string {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

const CONFIGURED_AGENTS_HOST = hostOf(process.env.CHAT_URL ?? AGENTS_ORIGIN_FALLBACK);

/** Trailing-slash-insensitive pathname split into non-empty segments. */
function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter(Boolean);
}

function classify(parsed: URL): AgentsDestination {
  const segments = segmentsOf(parsed.pathname);
  const params = parsed.searchParams;

  // Path-based routes first.
  if (segments[0] === 'chat') {
    if (!segments[1]) return 'other'; // bare /chat isn't a route we link to
    return segments[1] === 'new' ? 'new-chat' : 'existing-chat';
  }
  if (segments[0] === 'chats' && !segments[1]) return 'chats-list';
  if (segments[0] === 'artifacts' && !segments[1]) return 'artifacts';
  if (segments[0] === 'podcasts') {
    if (!segments[1]) return 'podcasts-list';
    // `create` must be checked before the {id} case — it's a literal route.
    return segments[1] === 'create' ? 'podcast-create' : 'podcast-project';
  }
  if (segments[0] === 'customize' && !segments[1]) return 'customize';

  // Root: the `?library=…` family. Narrowest first — a moments/media link also
  // carries `library`, so checking `library` first would swallow both.
  if (segments.length === 0) {
    if (params.has('moments')) return 'work-with-moments';
    if (params.has('media')) return 'work-with-media';
    if (params.has('library')) return 'work-with-library';
    return 'agents-home';
  }

  return 'other';
}

/**
 * Derive the destination page (and a human label) from an outbound URL.
 *
 * Never throws — a malformed or empty URL resolves to `other` so telemetry can
 * never break a navigation.
 *
 * `agentsHost` is injectable for tests; production callers omit it and get the
 * host from `CHAT_URL`.
 */
export function describeAgentsDestination(
  url: string,
  agentsHost: string = CONFIGURED_AGENTS_HOST,
): AgentsDestinationInfo {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return {
      destination: 'other',
      destinationLabel: DESTINATION_LABELS.other,
      isAgentsApp: false,
      targetHost: '',
    };
  }

  const isAgentsApp = !!agentsHost && parsed.hostname === agentsHost;

  // Route matching runs regardless of origin, so preview hosts
  // (*.experimental-agents.pages.dev) still resolve to a real destination.
  let destination = classify(parsed);

  // ...but `agents-home` asserts "the agents app's home page", which a non-agents
  // host cannot be. Without this, an allowed redirect to https://starzero.ai/
  // (marketing) or https://api.starzero.ai/ would be mislabelled as the agents
  // home. Only the bare-root case is ambiguous — a non-agents host carrying a
  // real route (a preview deploy) keeps its route.
  if (!isAgentsApp && destination === 'agents-home') destination = 'other';

  return {
    destination,
    destinationLabel: DESTINATION_LABELS[destination],
    isAgentsApp,
    targetHost: parsed.hostname,
  };
}

/* No name-truncation helper here on purpose: this event carries no
 * user-authored strings at all (see `AgentsExitContext`). If one is ever added,
 * that's a PII decision to make explicitly, not a length problem to paper over. */
